import React, { useEffect, useMemo, useState } from 'react';
import { faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useGetRequest from '../../../api/useGetRequest';
import AgvPageHeader, { PageHeaderButton } from '../../components/page-header';
import DropdownMenu from '../../components/dropdown-menu';
import { GridSearch, StatusDot } from '../../components/data-grid';
import GenericTableSkeleton from '../../../components/agv-generic-table/genericTableSkeleton';
import OnboardingBanner from '../../../components/onboarding/OnboardingBanner';
import { CARTO_TILES_URL } from '../../utils/map-tiles';
import CreateWarehouseModal from './create-warehouse-modal';
import useWarehouseActions from '../../api/useWarehouseActions';
import { toast } from 'react-toastify';
import './warehouses.css';

// Centre de la France, tant qu'aucun entrepôt n'est géolocalisé
const DEFAULT_CENTER = [46.603354, 1.888334];
const VISIBLE_VEHICLE_TAGS = 3;

const VIEWS = { ALL: 'ALL', MISSING_COORDINATES: 'MISSING_COORDINATES' };

const GEOLOCATED = { label: 'Géolocalisé', color: '#00b75f' };
const NOT_GEOLOCATED = { label: 'Non géolocalisé', color: '#b7791f' };

const escapeHtml = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[char]
  );

const createWarehouseIcon = (name, isSelected) =>
  L.divIcon({
    html: `<div class="wh-marker ${
      isSelected ? 'wh-marker--selected' : ''
    }"><span>▣</span><span class="wh-marker__name">${escapeHtml(
      name
    )}</span></div>`,
    className: 'wh-marker-wrapper',
    iconSize: null,
    iconAnchor: [0, 12],
  });

/**
 * Normalise une ligne de GET /factories
 */
const toWarehouse = (factory) => {
  const vehicleKeys = factory.vehicle_keys
    ? factory.vehicle_keys.split(',')
    : [];
  const vehicleLabels = factory.vehicle_labels
    ? factory.vehicle_labels.split(',')
    : [];
  // null/'' => non géolocalisé ; 0 reste une coordonnée valide
  const latitude = parseFloat(factory.latitude);
  const longitude = parseFloat(factory.longitude);
  const hasCoordinates =
    Number.isFinite(latitude) && Number.isFinite(longitude);

  return {
    id: factory.id,
    raw: factory,
    name: factory.name || 'Sans nom',
    externalId: factory.external_id,
    street: factory.street || '',
    city: [factory.zipcode, factory.city].filter(Boolean).join(' '),
    position: hasCoordinates ? [latitude, longitude] : null,
    vehicles: vehicleKeys.map((key, index) => ({
      key,
      label: vehicleLabels[index] || key,
    })),
    vehiclesCount: parseInt(factory.vehicles_count, 10) || 0,
  };
};

// Recadre la carte sur l'entrepôt sélectionné, ou sur l'ensemble
const MapFocus = ({ warehouses, selected }) => {
  const map = useMap();

  useEffect(() => {
    if (selected?.position) {
      map.flyTo(selected.position, Math.max(map.getZoom(), 11), {
        duration: 0.5,
      });
      return;
    }
    const points = warehouses.filter((w) => w.position).map((w) => w.position);
    if (points.length > 0) {
      map.fitBounds(L.latLngBounds(points), { padding: [60, 60], maxZoom: 11 });
    }
  }, [map, selected?.id, warehouses.length]);

  return null;
};

export default function Warehouses() {
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === 'true';

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // Entrepôt en cours de modification (ligne brute de GET /factories)
  const [editedWarehouse, setEditedWarehouse] = useState(null);
  const { archiveWarehouse } = useWarehouseActions();
  const [view, setView] = useState(VIEWS.ALL);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const { data, isLoading, mutate } = useGetRequest('/factories');

  const warehouses = useMemo(() => (data?.data || []).map(toWarehouse), [data]);
  const missingCoordinatesCount = warehouses.filter((w) => !w.position).length;
  const vehiclesCount = warehouses.reduce((sum, w) => sum + w.vehiclesCount, 0);

  const visibleWarehouses = useMemo(() => {
    const query = search.trim().toLowerCase();
    return warehouses.filter((warehouse) => {
      if (view === VIEWS.MISSING_COORDINATES && warehouse.position) {
        return false;
      }
      if (!query) return true;
      return [
        warehouse.name,
        warehouse.street,
        warehouse.city,
        warehouse.externalId,
        ...warehouse.vehicles.map((v) => v.key),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [warehouses, view, search]);

  const selected = warehouses.find((w) => w.id === selectedId) || null;

  const toggleSelection = (warehouse) =>
    setSelectedId((prev) => (prev === warehouse.id ? null : warehouse.id));

  const summary = [
    `${warehouses.length - missingCoordinatesCount} géolocalisé${
      warehouses.length - missingCoordinatesCount > 1 ? 's' : ''
    }`,
    `${vehiclesCount} véhicule${vehiclesCount > 1 ? 's' : ''} rattaché${
      vehiclesCount > 1 ? 's' : ''
    }`,
  ].join(' · ');

  /**
   * Archive un entrepôt sans véhicule rattaché : il disparaît de la liste et
   * n'est plus proposé comme dépôt (tournées et historique conservés)
   */
  const handleArchive = async (warehouse) => {
    if (
      !window.confirm(
        `Archiver l'entrepôt « ${warehouse.name} » ? Il ne sera plus proposé comme dépôt. Les tournées passées le conservent.`
      )
    ) {
      return;
    }
    try {
      await archiveWarehouse(warehouse.id);
      toast.success(`Entrepôt « ${warehouse.name} » archivé`);
      if (selectedId === warehouse.id) setSelectedId(null);
      mutate();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const renderRow = (warehouse) => {
    const geoStatus = warehouse.position ? GEOLOCATED : NOT_GEOLOCATED;
    const hiddenVehicles = warehouse.vehiclesCount - VISIBLE_VEHICLE_TAGS;

    return (
      <div
        key={warehouse.id}
        className={`agv-dg-row ${
          selectedId === warehouse.id ? 'agv-dg-row--selected' : ''
        }`}
        onClick={() => toggleSelection(warehouse)}
      >
        <div className="agv-dg-cell wh-cell--name">
          <FontAwesomeIcon icon={faWarehouse} className="wh-icon" />
          <span className="agv-dg-cell__stack">
            <span className="wh-name">{warehouse.name}</span>
            {warehouse.externalId && (
              <span className="agv-dg-cell__sub">
                Code {warehouse.externalId}
              </span>
            )}
          </span>
        </div>
        <div className="agv-dg-cell wh-cell--address">
          <span className="agv-dg-cell__stack">
            <span>{warehouse.street || 'Adresse non renseignée'}</span>
            {warehouse.city && (
              <span className="agv-dg-cell__sub">{warehouse.city}</span>
            )}
          </span>
        </div>
        <div className="agv-dg-cell wh-cell--geo">
          <span className="agv-dg-cell__stack">
            <StatusDot color={geoStatus.color} label={geoStatus.label} />
            {warehouse.position && (
              <span className="agv-dg-cell__sub wh-coords">
                {warehouse.position.map((v) => v.toFixed(4)).join(', ')}
              </span>
            )}
          </span>
        </div>
        <div className="agv-dg-cell wh-cell--vehicles">
          {warehouse.vehiclesCount === 0 ? (
            <span className="wh-muted">Aucun véhicule</span>
          ) : (
            <span className="wh-tags">
              {warehouse.vehicles.slice(0, VISIBLE_VEHICLE_TAGS).map((v) => (
                <span key={v.key} className="wh-tag" title={v.label}>
                  {v.key}
                </span>
              ))}
              {hiddenVehicles > 0 && (
                <span
                  className="wh-tag wh-tag--more"
                  title={warehouse.vehicles
                    .slice(VISIBLE_VEHICLE_TAGS)
                    .map((v) => v.key)
                    .join(', ')}
                >
                  +{hiddenVehicles}
                </span>
              )}
            </span>
          )}
        </div>
        <div className="agv-dg-cell agv-dg-cell--spacer" />
        <div
          className="agv-dg-cell agv-dg-cell--more"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu
            width={240}
            items={[
              {
                label: 'Voir sur la carte',
                disabled: !warehouse.position,
                hint: warehouse.position ? null : 'non géolocalisé',
                onClick: () => setSelectedId(warehouse.id),
              },
              { separator: true },
              {
                label: 'Modifier…',
                onClick: () => setEditedWarehouse(warehouse.raw),
              },
              {
                label: 'Archiver…',
                hint:
                  warehouse.vehiclesCount > 0
                    ? `${warehouse.vehiclesCount} véhicule${
                        warehouse.vehiclesCount > 1 ? 's' : ''
                      } rattaché${warehouse.vehiclesCount > 1 ? 's' : ''}`
                    : undefined,
                disabled: warehouse.vehiclesCount > 0,
                danger: true,
                onClick: () => handleArchive(warehouse),
              },
            ]}
            renderTrigger={({ toggle }) => (
              <button
                type="button"
                className="agv-dg-icon-btn"
                onClick={toggle}
                title="Actions"
              >
                ⋯
              </button>
            )}
          />
        </div>
      </div>
    );
  };

  const renderList = () => {
    if (isLoading) {
      return (
        <div className="agv-dg-grid__loading">
          <GenericTableSkeleton numberOfRows={6} />
        </div>
      );
    }
    if (warehouses.length === 0) {
      return (
        <div className="agv-dg-empty">
          <span className="agv-dg-empty__icon">▣</span>
          <span className="agv-dg-empty__title">Aucun entrepôt</span>
          <span className="agv-dg-empty__text">
            Les entrepôts sont les plateformes de départ de vos camions.
          </span>
          <button
            type="button"
            className="agv-dg-link"
            onClick={() => setIsCreateModalOpen(true)}
          >
            Créer un entrepôt ›
          </button>
        </div>
      );
    }
    if (visibleWarehouses.length === 0) {
      return (
        <div className="agv-dg-empty">
          <span className="agv-dg-empty__icon">⌕</span>
          <span className="agv-dg-empty__title">Aucun résultat</span>
          <span className="agv-dg-empty__text">
            Aucun entrepôt ne correspond à la recherche.
          </span>
        </div>
      );
    }
    return visibleWarehouses.map(renderRow);
  };

  return (
    <>
      <div className="wh-page">
        <AgvPageHeader
          icon={faWarehouse}
          breadcrumb={['Pilotage', 'Entrepôts']}
          title="Entrepôts"
          count={warehouses.length}
          summary={warehouses.length > 0 ? summary : null}
          activeTab={view}
          onTabChange={setView}
          tabs={[
            { key: VIEWS.ALL, icon: '☰', label: 'Tous les entrepôts' },
            {
              key: VIEWS.MISSING_COORDINATES,
              icon: '⚠',
              label: 'Non géolocalisés',
              count: missingCoordinatesCount,
            },
          ]}
          actions={
            <PageHeaderButton
              glyph="+"
              primary
              onClick={() => setIsCreateModalOpen(true)}
            >
              Créer un entrepôt
            </PageHeaderButton>
          }
        />

        {isOnboarding && (
          <div className="wh-onboarding">
            <OnboardingBanner
              step="1/2"
              title="Créez votre premier entrepôt"
              description="Les entrepôts sont les plateformes de départ de vos camions. Commencez par créer au moins un entrepôt pour continuer."
            />
          </div>
        )}

        <div className="wh-columns">
          <div className="agv-dg-grid wh-grid">
            <div className="agv-dg-toolbar">
              <div className="agv-dg-toolbar__filters">
                <GridSearch
                  value={search}
                  onChange={setSearch}
                  placeholder="Rechercher un entrepôt, une ville, un véhicule…"
                />
              </div>
              <div className="agv-dg-toolbar__controls">
                <span className="agv-dg-pagination__range">
                  {visibleWarehouses.length} sur {warehouses.length}
                </span>
              </div>
            </div>
            <div className="agv-dg-grid__scroll">
              <div className="agv-dg-grid__table wh-grid__table">
                <div className="agv-dg-grid__head">
                  <div className="agv-dg-cell wh-cell--name">Entrepôt</div>
                  <div className="agv-dg-cell wh-cell--address">Adresse</div>
                  <div className="agv-dg-cell wh-cell--geo">
                    Géolocalisation
                  </div>
                  <div className="agv-dg-cell wh-cell--vehicles">
                    Véhicules rattachés
                  </div>
                  <div className="agv-dg-cell agv-dg-cell--spacer" />
                  <div className="agv-dg-cell agv-dg-cell--more" />
                </div>
                <div className="agv-dg-grid__body">{renderList()}</div>
              </div>
            </div>
          </div>

          <div className="wh-map">
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={6}
              scrollWheelZoom
              className="wh-map__leaflet"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url={CARTO_TILES_URL}
              />
              <MapFocus warehouses={warehouses} selected={selected} />
              {warehouses
                .filter((warehouse) => warehouse.position)
                .map((warehouse) => (
                  <Marker
                    key={warehouse.id}
                    position={warehouse.position}
                    icon={createWarehouseIcon(
                      warehouse.name,
                      warehouse.id === selectedId
                    )}
                    zIndexOffset={warehouse.id === selectedId ? 1000 : 0}
                    eventHandlers={{ click: () => setSelectedId(warehouse.id) }}
                  >
                    <Popup>
                      <div className="wh-popup">
                        <strong>{warehouse.name}</strong>
                        {warehouse.street && <span>{warehouse.street}</span>}
                        {warehouse.city && <span>{warehouse.city}</span>}
                        <span className="wh-popup__meta">
                          {warehouse.vehiclesCount} véhicule
                          {warehouse.vehiclesCount > 1 ? 's' : ''} rattaché
                          {warehouse.vehiclesCount > 1 ? 's' : ''}
                        </span>
                      </div>
                    </Popup>
                  </Marker>
                ))}
            </MapContainer>
            {missingCoordinatesCount > 0 && (
              <button
                type="button"
                className="wh-map__notice"
                onClick={() => setView(VIEWS.MISSING_COORDINATES)}
              >
                ⚠ {missingCoordinatesCount} entrepôt
                {missingCoordinatesCount > 1 ? 's' : ''} absent
                {missingCoordinatesCount > 1 ? 's' : ''} de la carte
              </button>
            )}
          </div>
        </div>
      </div>

      <CreateWarehouseModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        onSuccess={() => mutate()}
      />
      <CreateWarehouseModal
        isOpen={Boolean(editedWarehouse)}
        setIsOpen={(open) => !open && setEditedWarehouse(null)}
        warehouse={editedWarehouse}
        onSuccess={() => mutate()}
      />
    </>
  );
}
